import React, { useEffect, useMemo, useState } from 'react';
import { StyledAmenitiesFilterDiv } from './index.styles';
import { CheckboxControlsList } from '@marriott/mi-ui-library';
import { AmenitiesFiltersComponentsProps } from '../../organisms/OSearchResults/index.types';
import { renderedData } from '../../organisms/OSearchResults/searchResultsMainComponent/index';
import {
  amenityCodeToIconMap,
  SearchFacetsList,
  SearchFacetsListCodes,
  tooltipAmenities,
} from '../../organisms/OSearchResults/index.constant';
import { SearchFacetsResponse, SearchStateFilterObj } from '../../organisms/OSearchResults/store/store.type';
import { formatNumberByLocale } from '../../utils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import { useConditionalSessionUpdateContext } from '../../hooks';
import { useConditionalPageContextSERP } from '../../hooks';
import { AmenitiesControlProps } from './index.types';
import { useConditionalSearchResultsRenderedDataSERP } from '../../hooks/lib/useConditionalRenderedData';
import { useConditionalSearchFilterContextWithSerpFlag } from '../../hooks/lib/useConditionalSearchFilterContext';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

export const AmenitiesFilterComponent: React.FC<AmenitiesFiltersComponentsProps> = ({
  onItemSelect,
  allFilterPopUp,
  clearFilterState,
  jsonContents,
  itemList,
  tooltipRefElement,
  setTooltipContent,
  modalRefElement,
  setShowToolTip,
  isDesktop,
}) => {
  const clickTrackingLoc = 'Phoenix Search Results';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { jsonContent }: any = useConditionalSearchResultsRenderedDataSERP(renderedData);
  const { currentLocale } = useConditionalPageContextSERP();
  const jsonContentData = jsonContents ?? jsonContent;
  const { searchQueryState } = useConditionalSearchFilterContextWithSerpFlag();
  const { interactionsSelected, setInteractionsSelected } = useConditionalSessionUpdateContext();
  const [currCheckedAmenitiesCode, setCurrCheckedAmenitiesCodeObj] = useState<SearchStateFilterObj | undefined>(
    searchQueryState[SearchFacetsList.amenities]
  );

  const getCurrentFilterArray = (): SearchFacetsResponse => {
    // update amenties
    return itemList
      ? itemList
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        searchQueryState?.filter?.filter((key: any) => key.type.code === SearchFacetsListCodes.amenities)[0];
  };

  const [currAvailableAmenities, setCurrAvailableAmenities] = useState<SearchFacetsResponse>(getCurrentFilterArray());

  const DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT = 8;
  useEffect(() => {
    setCurrAvailableAmenities(getCurrentFilterArray());
  }, [searchQueryState.filter, itemList]);

  useEffect(() => {
    if (clearFilterState === null || clearFilterState === undefined) {
      setCurrCheckedAmenitiesCodeObj(undefined);
    }
  }, [clearFilterState]);

  useEffect(() => {
    setCurrCheckedAmenitiesCodeObj(searchQueryState[SearchFacetsList.amenities]);
  }, [searchQueryState?.AMENITIES]);

  // Match the SearchFilterPanel behaviour: cap the list to N items unless user expands it.
  const shouldLimitAmenities =
    allFilterPopUp &&
    isEmpty(searchQueryState?.AMENITIES ?? {}) &&
    (currAvailableAmenities?.buckets?.length ?? 0) > DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT;

  // Build the ControlsProps array expected by CheckboxControlsList.
  // Doing this in a memo keeps the component from re-rendering the entire list on hover.
  const checkboxControls: AmenitiesControlProps[] = useMemo(() => {
    if (!currAvailableAmenities?.buckets) {
      return [];
    }
    return currAvailableAmenities.buckets.map(amenity => {
      const code = amenity?.code ? String(amenity.code) : '';
      const showTooltip = Object.prototype.hasOwnProperty.call(tooltipAmenities, code);
      const tooltipKey = tooltipAmenities[code as keyof typeof tooltipAmenities] as string;
      const tooltipTextContent = tooltipKey ? jsonContentData?.[tooltipKey] ?? '' : '';
      const formattedCount =
        amenity?.count !== undefined && amenity?.count !== null
          ? formatNumberByLocale(amenity?.count, currentLocale)
          : undefined;
      return {
        code,
        label: amenity?.label ?? '',
        count: formattedCount,
        iconClass: `icon-${amenityCodeToIconMap[code] ?? code ?? 'check'}`,
        showTooltip,
        tooltipData: showTooltip
          ? {
              tooltipHeading: amenity?.label ?? '',
              tooltipText: tooltipTextContent,
              tooltipCode: code,
            }
          : undefined,
        tooltipContainerId: 'tooltip-dialog',
      };
    });
  }, [currAvailableAmenities?.buckets]);

  // Map the stored search state back to the control objects so CheckboxControlsList
  // can highlight the correct selections without extra bookkeeping.
  const defaultSelectedControls: AmenitiesControlProps[] = useMemo(() => {
    if (!currCheckedAmenitiesCode) {
      return [];
    }
    const selectedCodes = Object.keys(currCheckedAmenitiesCode);
    return checkboxControls.filter(control => control.code && selectedCodes.includes(control.code));
  }, [checkboxControls, currCheckedAmenitiesCode]);

  // Convert CheckboxControlsList’s onChange payload back into the shape
  // expected by the amenities filter logic.
  const handleCheckboxListChange = (selectedValues: AmenitiesControlProps[]) => {
    const payload: SearchStateFilterObj = {};
    selectedValues.forEach(control => {
      if (control.code) {
        payload[control.code] = control.code;
      }
    });
    setCurrCheckedAmenitiesCodeObj(payload);
    setInteractionsSelected({ ...interactionsSelected, amenities: true });
    onItemSelect && onItemSelect(SearchFacetsList.amenities, payload);
  };

  // Hide tooltip when focus moves away from the icon or tooltip
  const hideTooltip = () => {
    setShowToolTip?.(false);
    // Skip ref mutation to avoid React Compiler warning
    // The ref will be cleaned up naturally when the component unmounts
  };

  useEffect(() => {
    hideTooltip();
  }, [currAvailableAmenities?.buckets]); // Re-run if array changes

  useEffect(() => {
    const modalContent = modalRefElement?.current;
    if (modalContent) {
      modalContent.addEventListener('scroll', hideTooltip);
    }
  }, [modalRefElement]);

  return currAvailableAmenities?.type?.count ? (
    <StyledAmenitiesFilterDiv className="t-font-alt-m filter-container">
      <>
        {checkboxControls.length > 0 && (
          <CheckboxControlsList
            name="amenitiesFilter"
            controls={checkboxControls}
            {...(allFilterPopUp && { title: jsonContentData?.amenitiesLabel })}
            enableShowMoreToggle={shouldLimitAmenities}
            showMoreCount={DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT}
            defaultSelected={defaultSelectedControls}
            onChange={handleCheckboxListChange}
            showMoreLabel={jsonContentData?.showMoreLabel}
            showLessLabel={jsonContentData?.showLessLabel}
            clickTrackingLoc={clickTrackingLoc}
            clickTrackingVal={`${jsonContentData?.amenitiesLabel ?? 'Amenities'} Section:`}
            tooltipRefElement={tooltipRefElement}
            setTooltipContent={setTooltipContent}
            modalRefElement={modalRefElement}
            setShowToolTip={setShowToolTip}
            colLength={isDesktop ? 6 : 12}
          />
        )}
      </>
      {allFilterPopUp ? <hr aria-hidden className="filter-separator" /> : ''}
    </StyledAmenitiesFilterDiv>
  ) : null;
};
